import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as fromDataServices from '../../data-services';
import { PayrollItem } from '../../payroll/item/store';
import {
    DynamicDialogRef,
    SelectItem
} from '../../shared/efaact-primeng.module';
import { LoggingService } from '../../shared/services/logging';

@Component({
    selector: 'ef-project-budget-add',
    templateUrl: './project-budget-add.component.html',
    styleUrls: ['./project-budget-add.component.scss'],
    standalone: false
})
export class ProjectBudgetAddComponent implements OnInit, OnDestroy {
    @Input() tasks: SelectItem[];
    @Input() serviceItems: SelectItem[];
    @Input() payrollItems: PayrollItem[];
    @Input() users: SelectItem[];
    @Input() costTypes: SelectItem[];
    @Input() costCenters: SelectItem[];
    @Input() contract: fromDataServices.Contract.Summary.ContractSummary;
    @Output() Save = new EventEmitter<any>();
    @Output() Cancel = new EventEmitter();
    public filteredPayrollItems$: Observable<SelectItem[]>;
    addBudgetFormGroup: UntypedFormGroup;
    dropdownItemHeight = environment.dropdownItemHeight;
    private active = true;
    constructor(
        public dialogRef: DynamicDialogRef,
        private log: LoggingService
    ) {
        this.addBudgetFormGroup = new UntypedFormGroup({
            TaskListID: new UntypedFormControl(null, Validators.required),
            CostType: new UntypedFormControl(null, Validators.required),
            SIListID: new UntypedFormControl(null),
            PIListID: new UntypedFormControl(null),
            CostCenterID: new UntypedFormControl(null),
            PersonListID: new UntypedFormControl(null),
            DisburseHours: new UntypedFormControl(null),
            DisburseAmount: new UntypedFormControl(null)
        });
    }

    ngOnInit(): void {
        const controls = this.addBudgetFormGroup.controls;

        this.filteredPayrollItems$ = controls['PersonListID'].valueChanges.pipe(
            takeWhile(() => this.active),
            tap(id =>
                this.log.verbose({
                    type: 'ProjectBudgetAddComponent:filteredPayrollItems$',
                    id,
                    payrollItems: this.payrollItems
                })
            ),
            switchMap(id =>
                of(
                    (this.payrollItems || []).filter(
                        p => p.EmployeeListID === this.optionValue(id)
                    )
                )
            ),
            map(items =>
                items.map(item => ({
                    label: item.PayrollItem,
                    value: item.PIListID
                }))
            ),
            tap(pi =>
                this.log.verbose({
                    type: 'ProjectBudgetAddComponent:filteredPayrollItems$',
                    pi
                })
            )
        );
    }
    onSaveClicked() {
        const value = this.addBudgetFormGroup.getRawValue();
        this.Save.next({
            ...value,
            TaskListID: this.optionValue(value.TaskListID),
            CostType: this.optionValue(value.CostType),
            SIListID: this.optionValue(value.SIListID),
            PIListID: this.optionValue(value.PIListID),
            CostCenterID: this.optionValue(value.CostCenterID),
            PersonListID: this.optionValue(value.PersonListID),
            DisburseHours:
                this.toNumber(value.DisburseHours) ??
                this.toNumber(this.inputValue('budgetDisburseHours')),
            DisburseAmount:
                this.toNumber(value.DisburseAmount) ??
                this.toNumber(this.inputValue('budgetDisburseAmount'))
        });
    }
    private inputValue(id: string): string | null {
        const el = document.getElementById(id) as HTMLInputElement | null;
        return el?.value ?? null;
    }
    private optionValue(value: unknown): unknown {
        if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            'value' in (value as object)
        ) {
            return (value as { value: unknown }).value;
        }
        return value;
    }
    private toNumber(value: unknown): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    ngOnDestroy(): void {
        this.active = false;
    }
}
